import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Music2,
  Pause,
  Play,
  Search,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import {
  fetchPublicSiteConfig,
  type SiteMusicConfig,
  type SiteMusicTrackItem,
} from "@/api/site-config.api";
import { usePlayerStore } from "@/store/player.store.ts";

type TrackWithKey = SiteMusicTrackItem & { key: string };

const PANEL_WIDTH = 312;
const EDGE_GAP = 12;
const DEFAULT_TOP = 80;
const COLLAPSED_WIDTH = 76;
const COLLAPSED_VISIBLE_WIDTH = 46;
const COLLAPSED_VISIBLE_WIDTH_HOVER = 58;
const LEFT_DOCK_THRESHOLD = 120;

function formatTime(second: number) {
  if (!Number.isFinite(second) || second < 0) return "00:00";
  const total = Math.floor(second);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function clampPosition(x: number, y: number, panelWidth: number) {
  const maxX = Math.max(EDGE_GAP, window.innerWidth - panelWidth - EDGE_GAP);
  const maxY = Math.max(EDGE_GAP, window.innerHeight - 140);
  return {
    x: Math.min(Math.max(x, EDGE_GAP), maxX),
    y: Math.min(Math.max(y, EDGE_GAP), maxY),
  };
}

const FloatingMusicPlayer: React.FC = () => {
  const [music, setMusic] = useState<SiteMusicConfig | null>(null);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volumeInput, setVolumeInput] = useState("70");
  const [collapsedHover, setCollapsedHover] = useState(false);
  const {
    visible,
    expanded,
    sideCollapsed,
    dockSide,
    position: storedPosition,
    volume,
    selectedAudioUrl,
    open,
    close,
    toggle,
    setExpanded,
    setSideCollapsed,
    setDockSide,
    setPosition: persistPosition,
    setVolume,
    setSelectedAudioUrl,
  } = usePlayerStore();

  const [position, setPosition] = useState(() => {
    if (storedPosition && storedPosition.x > 0) {
      return storedPosition;
    }
    return { x: window.innerWidth - PANEL_WIDTH - EDGE_GAP, y: DEFAULT_TOP };
  });

  const dragStateRef = useRef({
    dragging: false,
    pointerId: -1,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
  });

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const skipExpandClickRef = useRef(false);

  const savePosition = useCallback((next: { x: number; y: number }) => {
    persistPosition(next);
  }, [persistPosition]);

  useEffect(() => {
    let cancelled = false;

    fetchPublicSiteConfig().then((config) => {
      if (!cancelled) setMusic(config.music || null);
    });

    const onOpen = () => {
      open();
      setExpanded(true);
      setSideCollapsed(false);
    };

    const onToggle = () => {
      toggle();
    };

    const onResize = () => {
      setPosition((prev) => {
        if (sideCollapsed) {
          const maxY = Math.max(EDGE_GAP, window.innerHeight - 140);
          const y = Math.min(Math.max(prev.y, EDGE_GAP), maxY);
          const x = dockSide === "right"
            ? window.innerWidth - COLLAPSED_WIDTH - EDGE_GAP
            : EDGE_GAP;
          const next = { x, y };
          savePosition(next);
          return next;
        }

        const next = clampPosition(prev.x, prev.y, PANEL_WIDTH);
        savePosition(next);
        return next;
      });
    };

    window.addEventListener("music-player:open", onOpen);
    window.addEventListener("music-player:toggle", onToggle);
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("music-player:open", onOpen);
      window.removeEventListener("music-player:toggle", onToggle);
      window.removeEventListener("resize", onResize);
    };
  }, [dockSide, open, savePosition, setExpanded, setSideCollapsed, sideCollapsed, toggle]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    const onOutsideCollapse = (event: MouseEvent | TouchEvent) => {
      if (sideCollapsed || !visible) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;

      const side = position.x > window.innerWidth / 2 ? "right" : "left";
      setDockSide(side);
      setSideCollapsed(true);
      const next = {
        x: side === "right" ? window.innerWidth - COLLAPSED_WIDTH - EDGE_GAP : EDGE_GAP,
        y: Math.min(Math.max(position.y, EDGE_GAP), Math.max(EDGE_GAP, window.innerHeight - 140)),
      };
      setPosition(next);
      savePosition(next);
    };

    document.addEventListener("mousedown", onOutsideCollapse);
    document.addEventListener("touchstart", onOutsideCollapse, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onOutsideCollapse);
      document.removeEventListener("touchstart", onOutsideCollapse);
    };
  }, [position.x, position.y, savePosition, setDockSide, setSideCollapsed, sideCollapsed, visible]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!dragStateRef.current.dragging || event.pointerId !== dragStateRef.current.pointerId) return;
      event.preventDefault();

      const dx = event.clientX - dragStateRef.current.startX;
      const dy = event.clientY - dragStateRef.current.startY;
      if (!dragStateRef.current.moved && Math.hypot(dx, dy) > 4) {
        dragStateRef.current.moved = true;
      }

      if (sideCollapsed) {
        const nextY = dragStateRef.current.originY + dy;
        const maxY = Math.max(EDGE_GAP, window.innerHeight - 140);
        setPosition((prev) => ({
          x: prev.x,
          y: Math.min(Math.max(nextY, EDGE_GAP), maxY),
        }));
        return;
      }

      const nextX = dragStateRef.current.originX + dx;
      const nextY = dragStateRef.current.originY + dy;
      setPosition(clampPosition(nextX, nextY, PANEL_WIDTH));
    };

    const onPointerUp = (event: PointerEvent) => {
      if (!dragStateRef.current.dragging || event.pointerId !== dragStateRef.current.pointerId) return;

      const moved = dragStateRef.current.moved;
      dragStateRef.current.dragging = false;
      dragStateRef.current.pointerId = -1;
      dragStateRef.current.moved = false;

      document.body.style.userSelect = "";
      document.body.style.cursor = "";

      if (moved) {
        skipExpandClickRef.current = true;
      }

      if (!sideCollapsed) {
        const shouldDockLeft = position.x <= LEFT_DOCK_THRESHOLD;
        const shouldDockRight = position.x >= window.innerWidth - PANEL_WIDTH + LEFT_DOCK_THRESHOLD;
        if (shouldDockLeft || shouldDockRight) {
          const side = shouldDockLeft ? "left" : "right";
          setDockSide(side);
          setSideCollapsed(true);
          const maxY = Math.max(EDGE_GAP, window.innerHeight - 140);
          const y = Math.min(Math.max(position.y, EDGE_GAP), maxY);
          const next = {
            x: side === "right" ? window.innerWidth - COLLAPSED_WIDTH - EDGE_GAP : EDGE_GAP,
            y,
          };
          setPosition(next);
          savePosition(next);
          return;
        }
      }

      savePosition(position);
    };

    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [position.x, position.y, savePosition, setDockSide, setSideCollapsed, sideCollapsed]);

  useEffect(() => {
    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, []);

  const tracks = useMemo<TrackWithKey[]>(() => {
    if (!music?.enabled || !Array.isArray(music.tracks)) return [];

    return music.tracks
      .filter((item) => typeof item?.audioUrl === "string" && item.audioUrl.trim().length > 0)
      .map((item, index) => ({
        ...item,
        name: item.name?.trim() || `曲目 ${index + 1}`,
        key: `${index}-${item.audioUrl}`,
      }));
  }, [music]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const filteredTracks = useMemo(() => {
    const q = debouncedKeyword.trim().toLowerCase();
    if (!q) return tracks;
    return tracks.filter((item) => `${item.name} ${item.artist || ""}`.toLowerCase().includes(q));
  }, [tracks, debouncedKeyword]);

  useEffect(() => {
    if (!filteredTracks.length) {
      setSelectedKey("");
      return;
    }

    const storedMatch = selectedAudioUrl
      ? filteredTracks.find((item) => item.audioUrl === selectedAudioUrl)
      : null;
    if (storedMatch) {
      if (storedMatch.key !== selectedKey) setSelectedKey(storedMatch.key);
      return;
    }

    const exists = filteredTracks.some((item) => item.key === selectedKey);
    if (!exists) setSelectedKey(filteredTracks[0].key);
  }, [filteredTracks, selectedAudioUrl, selectedKey]);

  const current =
    filteredTracks.find((item) => item.key === selectedKey) ||
    filteredTracks[0] ||
    tracks[0] ||
    null;

  const canSwitch = filteredTracks.length > 1;

  const switchTrack = (direction: "prev" | "next") => {
    if (!canSwitch || !current) return;

    const currentIndex = filteredTracks.findIndex((item) => item.key === current.key);
    if (currentIndex < 0) {
      setSelectedKey(filteredTracks[0].key);
      return;
    }

    const nextIndex =
      direction === "prev"
        ? (currentIndex - 1 + filteredTracks.length) % filteredTracks.length
        : (currentIndex + 1) % filteredTracks.length;

    setSelectedKey(filteredTracks[nextIndex].key);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onEnded = () => {
      if (canSwitch) switchTrack("next");
      else setPlaying(false);
    };

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);

    return () => {
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, [canSwitch, filteredTracks, selectedKey]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current?.audioUrl) return;

    setCurrentTime(0);
    setDuration(0);
    audio.src = current.audioUrl;
    audio.load();
    setSelectedAudioUrl(current.audioUrl);

    if (music?.autoplay) {
      audio.play().catch(() => setPlaying(false));
    } else {
      setPlaying(false);
    }
  }, [current?.audioUrl, music?.autoplay, setSelectedAudioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
    audio.muted = volume <= 0;
  }, [volume]);

  useEffect(() => {
    setVolumeInput(String(Math.round(volume * 100)));
  }, [volume]);

  const onDragStart = (event: React.PointerEvent<HTMLElement>) => {
    dragStateRef.current.dragging = true;
    dragStateRef.current.pointerId = event.pointerId;
    dragStateRef.current.startX = event.clientX;
    dragStateRef.current.startY = event.clientY;
    dragStateRef.current.originX = position.x;
    dragStateRef.current.originY = position.y;
    dragStateRef.current.moved = false;

    event.currentTarget.setPointerCapture(event.pointerId);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !current?.audioUrl) return;

    if (audio.paused) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  };

  const onSeek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const onVolumeInputChange = (value: string) => {
    setVolumeInput(value);
    if (value.trim() === "") return;
    const next = Number(value);
    if (!Number.isFinite(next)) return;
    const clamped = Math.max(0, Math.min(100, next));
    setVolume(clamped / 100);
  };

  const expandFromCollapsed = () => {
    setSideCollapsed(false);
    setPosition((prev) =>
      (() => {
        const next = clampPosition(
          dockSide === "right" ? window.innerWidth - PANEL_WIDTH - EDGE_GAP : EDGE_GAP,
          prev.y,
          PANEL_WIDTH,
        );
        savePosition(next);
        return next;
      })(),
    );
  };

  useEffect(() => {
    if (!storedPosition || storedPosition.x <= 0) return;
    setPosition(storedPosition);
  }, [storedPosition.x, storedPosition.y]);

  if (!music?.enabled || !visible) return null;

  return (
    <>
      <audio ref={audioRef} preload="metadata" />
      {sideCollapsed ? (
        <aside
        className="fixed z-[70] transition-[top] duration-300 ease-out"
        style={{
          right: dockSide === "right" ? 0 : "auto",
          left: dockSide === "left" ? 0 : "auto",
          top: `${position.y}px`,
          transform: dockSide === "right"
            ? `translateX(calc(100% - ${collapsedHover ? COLLAPSED_VISIBLE_WIDTH_HOVER : COLLAPSED_VISIBLE_WIDTH}px))`
            : `translateX(calc(-100% + ${collapsedHover ? COLLAPSED_VISIBLE_WIDTH_HOVER : COLLAPSED_VISIBLE_WIDTH}px))`,
          transition: "transform 220ms ease, top 300ms ease",
        }}
        onMouseEnter={() => setCollapsedHover(true)}
        onMouseLeave={() => setCollapsedHover(false)}
      >
        <div
          className={`relative h-11 w-[76px] rounded-full border shadow-md flex items-center justify-center ${
            collapsedHover ? "animate-[playerCollapsedHint_900ms_ease-in-out_infinite]" : "animate-[playerDockIn_.28s_ease-out]"
          }`}
          style={{
            backgroundColor: "var(--bg-card)",
            borderColor: "var(--border-light)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (skipExpandClickRef.current) {
                skipExpandClickRef.current = false;
                return;
              }
              expandFromCollapsed();
            }}
            className={`h-full w-full rounded-full inline-flex items-center gap-1 hover:bg-blue-100/80 dark:hover:bg-blue-800/40 ${
              dockSide === "right" ? "justify-start pl-2.5" : "justify-end pr-2.5"
            }`}
            style={{ color: "var(--text-primary)" }}
            title="展开播放器"
          >
            {dockSide === "left" && (
              <ChevronRight
                size={15}
                className={`transition-all duration-200 ${collapsedHover ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-1"}`}
              />
            )}
            <span
              className="inline-flex h-7 w-7 items-center justify-center"
              style={{
                color: "var(--text-primary)",
              }}
            >
              <Music2 size={20} strokeWidth={2.35} />
            </span>
            {dockSide === "right" && (
              <ChevronLeft
                size={15}
                className={`transition-all duration-200 ${collapsedHover ? "opacity-100 translate-x-0" : "opacity-0 translate-x-1"}`}
              />
            )}
          </button>

          <button
            type="button"
            onPointerDown={(event) => {
              event.stopPropagation();
              onDragStart(event as React.PointerEvent<HTMLElement>);
            }}
            className={`absolute ${dockSide === "right" ? "left-0" : "right-0"} top-1/2 -translate-y-1/2 h-8 w-3 cursor-move active:cursor-grabbing`}
            style={{ touchAction: "none" }}
            title="拖拽位置"
          />
        </div>
      </aside>
      ) : (
        <aside
      ref={panelRef}
      className="fixed z-[70] w-[312px] max-w-[calc(100vw-2rem)] rounded-xl border border-blue-200/70 dark:border-blue-700/50 bg-[var(--bg-card)] shadow-[0_10px_26px_-18px_rgba(37,99,235,0.45)] transition-[left,top,opacity,transform] duration-300 ease-out animate-[playerPanelIn_.28s_ease-out]"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      <div
        className={`px-3 py-2.5 ${expanded ? "border-b border-[var(--border-light)]" : ""}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div
            onPointerDown={onDragStart}
            className="min-w-0 flex-1 cursor-move active:cursor-grabbing"
            style={{ touchAction: "none" }}
            title="拖拽播放器"
          >
            <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-700 dark:text-blue-300">
              <Music2 size={13} />
              <span className="truncate">{music.title || "音乐播放器"}</span>
            </div>
            <p className="text-[11px] text-[var(--text-secondary)] truncate mt-0.5">
              {current?.artist ? `${current.artist} · ${current.name}` : current?.name || "暂无音源"}
            </p>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="h-6 w-6 inline-flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100/70 dark:hover:bg-blue-900/40"
              title={expanded ? "收起内容" : "展开内容"}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
            </button>
            <button
              type="button"
              onClick={() => {
                setDockSide(position.x > window.innerWidth / 2 ? "right" : "left");
                setSideCollapsed(true);
                setPosition((prev) => {
                  const side = prev.x > window.innerWidth / 2 ? "right" : "left";
                  const next = {
                    x: side === "right" ? window.innerWidth - COLLAPSED_WIDTH - EDGE_GAP : EDGE_GAP,
                    y: Math.min(Math.max(prev.y, EDGE_GAP), Math.max(EDGE_GAP, window.innerHeight - 140)),
                  };
                  savePosition(next);
                  return next;
                });
              }}
              className="h-6 w-6 inline-flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100/70 dark:hover:bg-blue-900/40"
              title="收起到侧边"
            >
              <ChevronRight size={14} />
            </button>
            <button
              type="button"
              onClick={close}
              className="h-6 w-6 inline-flex items-center justify-center rounded-md text-[var(--text-secondary)] hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-100/70 dark:hover:bg-blue-900/40"
              title="关闭"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => switchTrack("prev")}
                disabled={!canSwitch || !tracks.length}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--border-light)] text-[var(--text-secondary)] hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40"
                title="上一首"
              >
                <SkipBack size={13} />
              </button>
              <button
                type="button"
                onClick={togglePlay}
                disabled={!tracks.length}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-blue-500/70 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                title={playing ? "暂停" : "播放"}
              >
                {playing ? <Pause size={13} /> : <Play size={13} className="translate-x-[1px]" />}
              </button>
              <button
                type="button"
                onClick={() => switchTrack("next")}
                disabled={!canSwitch || !tracks.length}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--border-light)] text-[var(--text-secondary)] hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-40"
                title="下一首"
              >
                <SkipForward size={13} />
              </button>
            </div>

            <label className="h-7 px-1.5 inline-flex items-center justify-center gap-1 rounded-md border border-[var(--border-light)] text-[var(--text-secondary)]">
              {volume <= 0 ? <VolumeX size={13} /> : <Volume2 size={13} />}
              <input
                type="number"
                min={0}
                max={100}
                value={volumeInput}
                onChange={(e) => onVolumeInputChange(e.target.value)}
                disabled={!tracks.length}
                className="w-10 bg-transparent text-[11px] text-[var(--text-primary)] outline-none"
              />
              <span className="text-[11px]">%</span>
            </label>
          </div>

          <div className="mt-2 mb-1 flex items-center gap-2 text-[11px] text-[var(--text-secondary)]">
            <span className="w-9 text-right">{formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={Math.min(currentTime, duration || 0)}
              onChange={(e) => onSeek(Number(e.target.value))}
              disabled={!tracks.length}
              className="h-1.5 flex-1 accent-blue-600"
            />
            <span className="w-9">{formatTime(duration)}</span>
          </div>
        </div>
      </div>

      <div
        className={`grid transition-all duration-300 ease-out ${
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className={expanded ? "overflow-visible" : "overflow-hidden"}>
          <div className="px-3 py-2.5">
            <div className="mb-2.5 flex items-center gap-2.5">
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-[var(--border-light)] bg-[var(--bg-secondary)]">
                {current?.coverUrl ? (
                  <img src={current.coverUrl} alt={current.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[var(--text-secondary)]">
                    <Music2 size={16} />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{current?.name || "暂无音源"}</p>
                <p className="truncate text-[11px] text-[var(--text-secondary)]">
                  {current?.artist || "请在后台-站点内容管理-音乐播放器中新增音源"}
                </p>
              </div>
            </div>

            <div className="relative mb-1" ref={dropdownRef}>
              <label className="relative block">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <input
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    if (!dropdownOpen) setDropdownOpen(true);
                  }}
                  onFocus={() => setDropdownOpen(true)}
                  placeholder={current?.name || "搜索或选择音乐"}
                  disabled={!tracks.length}
                  className="w-full h-8 pl-7 pr-7 text-[11px] rounded-md border border-[var(--border-light)] bg-transparent text-[var(--text-primary)]"
                />
                <button
                  type="button"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  disabled={!tracks.length}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center text-[var(--text-secondary)]"
                  title="展开曲目列表"
                >
                  <ChevronDown size={12} className={dropdownOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
              </label>

              {dropdownOpen && tracks.length > 0 && (
                <ul className="absolute z-[80] mt-1 w-full max-h-52 overflow-auto rounded-md border border-[var(--border-light)] bg-[var(--bg-card)] shadow-lg">
                  {filteredTracks.length > 0 ? (
                    filteredTracks.map((item) => (
                      <li key={item.key}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedKey(item.key);
                            setKeyword("");
                            setDropdownOpen(false);
                          }}
                          className="w-full px-2 py-2 text-[11px] text-left flex items-center justify-between text-[var(--text-primary)] hover:bg-blue-50/80 dark:hover:bg-blue-900/30"
                        >
                          <span className="truncate">{item.artist ? `${item.artist} · ${item.name}` : item.name}</span>
                          {item.key === current?.key ? <Check size={12} /> : null}
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-2 py-2 text-xs text-[var(--text-secondary)]">无匹配曲目</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes playerDockIn {
          from { opacity: 0; transform: translateX(14px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes playerPanelIn {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes playerCollapsedHint {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-4px); }
        }
      `}</style>
    </aside>
      )}
    </>
  );
};

export default FloatingMusicPlayer;
